import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AssetDistributionChart = () => {
  const [viewType, setViewType] = useState('pie');
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const departmentData = [
    { name: 'IT Department', value: 145, color: '#2563EB', assets: 145, cost: 287500 },
    { name: 'Finance', value: 89, color: '#059669', assets: 89, cost: 156300 },
    { name: 'Marketing', value: 67, color: '#D97706', assets: 67, cost: 134000 },
    { name: 'Operations', value: 54, color: '#DC2626', assets: 54, cost: 98700 },
    { name: 'HR', value: 32, color: '#7C3AED', assets: 32, cost: 67800 },
    { name: 'Sales', value: 28, color: '#0EA5E9', assets: 28, cost: 52400 }
  ];

  const assetTypeData = [
    { category: 'Laptops', IT: 65, Finance: 45, Marketing: 32, Operations: 28, HR: 18, Sales: 15 },
    { category: 'Desktops', IT: 35, Finance: 25, Marketing: 18, Operations: 15, HR: 8, Sales: 7 },
    { category: 'Monitors', IT: 25, Finance: 12, Marketing: 10, Operations: 8, HR: 4, Sales: 4 },
    { category: 'Printers', IT: 12, Finance: 4, Marketing: 4, Operations: 2, HR: 1, Sales: 1 },
    { category: 'Servers', IT: 8, Finance: 3, Marketing: 3, Operations: 1, HR: 1, Sales: 1 }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevated">
          <p className="font-medium text-popover-foreground">{label || data?.name}</p>
          <p className="text-sm text-muted-foreground">
            Assets: <span className="font-medium text-popover-foreground">{data?.value || data?.assets}</span>
          </p>
          {data?.cost && (
            <p className="text-sm text-muted-foreground">
              Value: <span className="font-medium text-popover-foreground">₹{data?.cost?.toLocaleString('en-IN')}</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const handleDepartmentClick = (department) => {
    setSelectedDepartment(selectedDepartment === department ? null : department);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Asset Distribution</h3>
          <p className="text-sm text-muted-foreground">Assets by department and category</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant={viewType === 'pie' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('pie')}
            iconName="PieChart"
            iconPosition="left"
          >
            Pie Chart
          </Button>
          <Button
            variant={viewType === 'bar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewType('bar')}
            iconName="BarChart3"
            iconPosition="left"
          >
            Bar Chart
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart Section */}
        <div className="lg:col-span-2">
          <div className="h-80">
            {viewType === 'pie' ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={departmentData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    onClick={(data) => handleDepartmentClick(data?.name)}
                    className="cursor-pointer"
                  >
                    {departmentData?.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry?.color}
                        stroke={selectedDepartment === entry?.name ? '#1E293B' : 'none'}
                        strokeWidth={selectedDepartment === entry?.name ? 2 : 0}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={assetTypeData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="category" 
                    tick={{ fontSize: 12 }}
                    stroke="#64748B"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    stroke="#64748B"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="IT" stackId="a" fill="#2563EB" />
                  <Bar dataKey="Finance" stackId="a" fill="#059669" />
                  <Bar dataKey="Marketing" stackId="a" fill="#D97706" />
                  <Bar dataKey="Operations" stackId="a" fill="#DC2626" />
                  <Bar dataKey="HR" stackId="a" fill="#7C3AED" />
                  <Bar dataKey="Sales" stackId="a" fill="#0EA5E9" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Department Details */}
        <div className="space-y-4">
          <h4 className="font-medium text-card-foreground">Department Breakdown</h4>
          <div className="space-y-3">
            {departmentData?.map((dept) => (
              <div
                key={dept?.name}
                className={`p-3 rounded-lg border cursor-pointer transition-smooth ${
                  selectedDepartment === dept?.name
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
                onClick={() => handleDepartmentClick(dept?.name)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: dept?.color }}
                    />
                    <span className="text-sm font-medium text-card-foreground">
                      {dept?.name}
                    </span>
                  </div>
                  <Icon 
                    name={selectedDepartment === dept?.name ? "ChevronUp" : "ChevronDown"} 
                    size={16} 
                    className="text-muted-foreground" 
                  />
                </div>
                
                <div className="text-xs text-muted-foreground space-y-1">
                  <div className="flex justify-between">
                    <span>Assets:</span>
                    <span className="font-medium">{dept?.assets}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Value:</span>
                    <span className="font-medium">₹{dept?.cost?.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                {selectedDepartment === dept?.name && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">Top Asset Types:</p>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span>Laptops</span>
                        <span className="font-medium">
                          {assetTypeData?.[0]?.[dept?.name?.split(' ')?.[0]] || 0}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>Desktops</span>
                        <span className="font-medium">
                          {assetTypeData?.[1]?.[dept?.name?.split(' ')?.[0]] || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetDistributionChart;